import { API_URL, getCredentials, processError, publicPostFetch } from "../../shared/utils/Util";

// Types
export interface RestaurantUser {
  user_id: string;
  email: string;
  roles: string[];
}

export interface ListUsersResponse {
  users: RestaurantUser[];
}

export interface CheckUserExistsResponse {
  exists: boolean;
}

export interface CreateUserRequest {
  email: string;
  roles: string[];
  password?: string;
}

export interface CreateUserResponse {
  user_id: string;
  is_new_user: boolean;
}

export interface UpdateUserRolesRequest {
  roles: string[];
}

export interface ResetPasswordRequest {
  new_password: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

// API Functions
export const listUsers = (
  restaurantId: string,
  onSuccess: (data: ListUsersResponse) => void,
  onError: (err: unknown) => void
): void => {
  fetch(`${API_URL}/v1/r/${restaurantId}/admin/users`, {
    credentials: "include",
    headers: {
      Authorization: `Basic ${getCredentials()}`,
    },
  })
    .then((res) => {
      if (!res.ok) throw res;
      return res.json();
    })
    .then(onSuccess)
    .catch((err) => processError(err, onError));
};

export const checkUserExists = (
  restaurantId: string,
  email: string,
  onSuccess: (data: CheckUserExistsResponse) => void,
  onError: (err: unknown) => void
): void => {
  fetch(`${API_URL}/v1/r/${restaurantId}/admin/users/check?email=${encodeURIComponent(email)}`, {
    credentials: "include",
    headers: {
      Authorization: `Basic ${getCredentials()}`,
    },
  })
    .then((res) => {
      if (!res.ok) throw res;
      return res.json();
    })
    .then(onSuccess)
    .catch((err) => processError(err, onError));
};

export const createUser = (
  restaurantId: string,
  data: CreateUserRequest,
  onSuccess: (data: CreateUserResponse) => void,
  onError: (err: unknown) => void
): void => {
  fetch(`${API_URL}/v1/r/${restaurantId}/admin/users`, {
    credentials: "include",
    method: "POST",
    headers: {
      Authorization: `Basic ${getCredentials()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
    .then((res) => {
      if (!res.ok) throw res;
      return res.json();
    })
    .then(onSuccess)
    .catch((err) => processError(err, onError));
};

export const updateUserRoles = (
  restaurantId: string,
  userId: string,
  data: UpdateUserRolesRequest,
  onSuccess: () => void,
  onError: (err: unknown) => void
): void => {
  fetch(`${API_URL}/v1/r/${restaurantId}/admin/users/${userId}`, {
    credentials: "include",
    method: "PUT",
    headers: {
      Authorization: `Basic ${getCredentials()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
    .then((res) => {
      if (!res.ok) throw res;
      onSuccess();
    })
    .catch((err) => processError(err, onError));
};

export const removeUser = (
  restaurantId: string,
  userId: string,
  onSuccess: () => void,
  onError: (err: unknown) => void
): void => {
  fetch(`${API_URL}/v1/r/${restaurantId}/admin/users/${userId}`, {
    credentials: "include",
    method: "DELETE",
    headers: {
      Authorization: `Basic ${getCredentials()}`,
    },
  })
    .then((res) => {
      if (!res.ok) throw res;
      onSuccess();
    })
    .catch((err) => processError(err, onError));
};

export const resetUserPassword = (
  restaurantId: string,
  userId: string,
  data: ResetPasswordRequest,
  onSuccess: () => void,
  onError: (err: unknown) => void
): void => {
  fetch(`${API_URL}/v1/r/${restaurantId}/admin/users/${userId}/reset_password`, {
    credentials: "include",
    method: "POST",
    headers: {
      Authorization: `Basic ${getCredentials()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
    .then((res) => {
      if (!res.ok) throw res;
      onSuccess();
    })
    .catch((err) => processError(err, onError));
};

export const sendUserInvite = (
  restaurantId: string,
  userId: string,
  onSuccess: () => void,
  onError: (err: unknown) => void
): void => {
  fetch(`${API_URL}/v1/r/${restaurantId}/admin/users/${userId}/send_invite`, {
    credentials: "include",
    method: "POST",
    headers: {
      Authorization: `Basic ${getCredentials()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  })
    .then((res) => {
      if (!res.ok) throw res;
      onSuccess();
    })
    .catch((err) => processError(err, onError));
};

export const changePassword = (
  data: ChangePasswordRequest,
  onSuccess: () => void,
  onError: (err: unknown) => void
): void => {
  fetch(`${API_URL}/v1/u/password`, {
    credentials: "include",
    method: "PUT",
    headers: {
      Authorization: `Basic ${getCredentials()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
    .then((res) => {
      if (!res.ok) throw res;
      onSuccess();
    })
    .catch((err) => processError(err, onError));
};

// Public API (no authentication required)
export interface AcceptInviteRequest {
  token_id: string;
  secret: string;
  new_password: string;
}

export const acceptInvite = (
  data: AcceptInviteRequest,
  onSuccess: () => void,
  onError: (err: unknown) => void
): void => {
  publicPostFetch(`${API_URL}/v1/public/u/user_invite`, data, onSuccess, onError);
};
